import { useEffect } from "react";

const GTM_ID = "GTM-WR46MP54";

/**
 * Loads Google Tag Manager after hydration (and after the browser is idle),
 * so it never competes with the first paint. The dataLayer is created
 * immediately so any early pushes are preserved.
 */
export function DeferredGTM() {
  useEffect(() => {
    const w = window as unknown as { dataLayer?: unknown[]; __gtmLoaded?: boolean };
    w.dataLayer = w.dataLayer || [];
    if (w.__gtmLoaded) return;

    const load = () => {
      if (w.__gtmLoaded) return;
      w.__gtmLoaded = true;
      w.dataLayer!.push({ "gtm.start": Date.now(), event: "gtm.js" });
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
      document.head.appendChild(script);
    };

    const idle = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;
    const timer = idle ? idle(load, { timeout: 2500 }) : window.setTimeout(load, 1500);

    // Any real interaction (a possible affiliate click) loads it right away,
    // so conversion events are never dropped.
    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "touchstart"];
    events.forEach((e) => window.addEventListener(e, load, { once: true, passive: true }));

    return () => {
      if (!idle) window.clearTimeout(timer as number);
      events.forEach((e) => window.removeEventListener(e, load));
    };
  }, []);

  return null;
}
